import FileLibraryHelper from "@/helpers/cms/FileLibraryHelper.js";
import {getLanguage, tr} from "@vue-components/helpers/I18nPluginHelper.js";
import MapBaseClass from "./MapBaseClass.js";

export default class MapItem extends MapBaseClass {
    get ignoredFields() {
        return [`map`, `links`, `settings`];
    }

    constructor(data, map) {
        super(data);
        this.map = map;

        this.links = [];

        if (!this.navRule) {
            // Define if the cost of taking this item should be higher
            // recommended | default | not-recommended | avoid | unavailable
            this.navRule = `default`;
        }
        // NavCost is based on distance and navRule
        this.navCost = 0;

        this.offsetAngle = this.image360?.offsetAngle || 0;
        this.image360?.linkedMapItemsOffsets && (this.linkedMapItemsOffsets = this.image360.linkedMapItemsOffsets);
        this.image360 = FileLibraryHelper.getCacheUrl(this.image360);
        this.image = FileLibraryHelper.getCacheUrl(this.image);

        // old compatibility
        if (!this.floors) {
            this.floors = {
                list: {},
                multiple: false,
                levelOffset: 0 // when the ground is not flat and elevators have different floors
            };

            if (this.multiFloors) {
                this.floors.multiple = true;
                delete this.multiFloors;

                for (let id of data.floors) {
                    this.floors.list[id] = {
                        accessible: true,
                        name: null
                    };
                }
            }
        }

        this.updateNavCost();
    }

    getName() {
        /*if (this.cmsItem) {
            return tr(this.cmsItem.name);
        } else */if (this.label) {
            return tr(this.label);
        } else if (this.name) {
            return this.name;
        } else {
            return null;
        }
    }

    getSubtitle() {
        if (this.subtitle) {
            return tr(this.subtitle);
        } else if (this.address) {
            return tr(this.address);
        } else if (this.cmsItem) {
            return tr(this.cmsItem.getSecondaryValue());
        } else {
            return null;
        }
    }

    getIcon() {
        switch (this.type) {
            case `toilet`:
                if (this.accessible) {
                    return getPublicIcon(`restroom-accessible`);
                } else {
                    return getPublicIcon(`restroom`);
                }
            default:
                return getPublicIcon(this.type);
        }
    }

    getGlyph(fontAwesomePrefix = `fas`) {
        let glyph = this.glyph;
        if (this.icon?.glyph) {
            glyph = this.icon.glyph;
        }
        else if (this.cmsItem &&
            this.map.integrations.cms.icons?.enabled &&
            this.cmsItem[this.map.integrations.cms.icons.field]) {
            glyph = this.cmsItem[this.map.integrations.cms.icons.field];
        } else {
            glyph = glyph || `fa-location-dot`;
        }
        if (glyph && /^(fak |fab |fas |fal |far ).*/.test(glyph) === false) {
            return `${fontAwesomePrefix} ${glyph}`;
        }
        return glyph;
    }

    getItemColor() {
        if (this.icon?.bg !== null) {
            return this.icon.color;
        }
        return null;
    }

    getFullName() {
        if (this.cmsItem) {
            if (this.cmsItem.hasMultipleMapItems()) {
                // In the case of elevators, use the name on the map to identify uniquely each item
                return this.getName();
            } else {
                return tr(this.cmsItem.getName());
            }
        } else {
            return this.getName();
        }
    }

    getAddress() {
        if (this.address) {
            return this.address;
        } else if (this.cmsItem?.address) {
            return this.cmsItem.address;
        } else {
            return null;
        }
    }

    getLocationDescription() {
        if (tr(this.location)) {
            return tr(this.location);
        } else {
            if (this.cmsItem) {
                return this.getFullName();
            }
            switch (this.type) {
                case `elevator`:
                    return tr(`The Elevators|Les ascenseurs`);
                case `stairs`:
                    return tr(`The Stairs|Les escaliers`);
                case `door`:
                    return tr(`The Door|La porte`);
                case `escalator`:
                    return tr(`The Escalator|L'escalator`);
                case `emergency-exit`:
                    return tr(`The Emergency Exit|La sortie de secours`);
                case `entrance`:
                    if (this.label) {
                        return tr(this.label);
                    } else {
                        return tr(`The Entrance/Exit|L'entrée/sortie`);
                    }
                case `atm`:
                    return tr(`The ATM|Le guichet bancaire`);
            }
        }
    }

    matchPermalink(permalink) {
        if (this.permalink && typeof this.permalink === `string` && permalink === this.permalink) {
            return true;
        } else if (this.permalink && typeof this.permalink === `object` &&
            (permalink === this.permalink.en || permalink === this.permalink.default || permalink === this.permalink.fr)) {
            return true;
        }
        return permalink === this.id;
    }

    getPermalink() {
        if (this.permalink) {
            return tr(this.permalink);
        } else {
            return this.id;
        }
    }

    setCmsItem(cmsItem) {
        this.cmsItem = cmsItem;
        if (this.cmsItem) {
            this.permalink = this.cmsItem.permalink;
        }
    }

    matchSearch(searchRegexp) {
        if (this.cmsItem) {
            return this.cmsItem.matchSearch(searchRegexp);
        } else {
            return searchRegexp.test(this.name);
        }
    }

    updateNavCost() {
        this.navCost = 0;
        if (this.type === `elevator`) {
            this.navCost += 20;
        } else if (this.type === `stairs`) {
            this.navCost += 30;
        }
        switch (this.navRule) {
            case `recommended`:
                this.navCost -= 5;
                break;
            case `not-recommended`:
                this.navCost += 5;
                break;
            case `avoid`:
                this.navCost += 10;
                break;
            case `unavailable`:
                this.navCost += 9999;
                break;
        }
    }

    getNavCost(preferences) {
        if (preferences?.accessible) {
            if (this.type === `stairs` ||
                (this.type === `toilet` && this.accessible === false)) {
                return 9999;
            }
        }
        return this.navCost;
    }

    // Note to prevent access to one specific floor, ensure there is no link
    isOnFloor(floorId) {
        return this.floorId === floorId ||
            (this.floors.multiple && this.floors.list[floorId]?.accessible);
    }

    addLink(link) {
        // const existingLink = this.links.find(l => l.id === linkedItem.id);
        this.links.push(link)
    }

    removeLink(link) {
        this.links.removeItem(link);
    }
}
